import { type RefObject, useEffect } from "react";
import type { ActiveFilterEnum } from "./types";

export type VisibleCategory = {
	category: Exclude<ActiveFilterEnum, ActiveFilterEnum.ALL>;
	resultCount: number;
};

export const useStickyScrollBehavior = (
	scrollRef: RefObject<HTMLDivElement | null>,
	pinnedContainerRef: RefObject<HTMLDivElement | null>,
	visibleCategories: VisibleCategory[],
): void => {
	useEffect(() => {
		const scroll = scrollRef.current;
		const pinnedContainer = pinnedContainerRef.current;
		if (!scroll || !pinnedContainer) return;

		const elements = visibleCategories.map((vc) => ({
			category: vc,
			watcher: scroll.querySelector<HTMLElement>(`[data-category-watcher="${vc.category}"]`),
			pinnedButton: pinnedContainer.querySelector<HTMLElement>(
				`[data-pinned-category="${vc.category}"]`,
			),
		}));

		const compute = () => {
			const scrollRect = scroll.getBoundingClientRect();
			let hasPinned = false;

			for (const el of elements) {
				if (!el.watcher || !el.pinnedButton) continue;

				if (el.watcher.getBoundingClientRect().top >= scrollRect.bottom) {
					el.pinnedButton.removeAttribute("data-hidden");
					hasPinned = true;
				} else {
					el.pinnedButton.setAttribute("data-hidden", "");
				}
			}

			pinnedContainer.style.display = hasPinned ? "" : "none";
		};

		scroll.addEventListener("scroll", compute, { passive: true });
		compute();

		return () => {
			scroll.removeEventListener("scroll", compute);
		};
	}, [scrollRef, pinnedContainerRef, visibleCategories]);
};
